import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyId } from './useCompanyId';
import { useStrategyCalls } from './useStrategyCalls';
import { findAccountIdByEmail } from '../services/firestoreService';
import {
  listTaskTimeRequests,
  computeTaskTimeBalance,
  computeAllowanceFromCallLimit,
} from '../services/taskTimeService';

/**
 * Pooled Implementation Hours balance for the current billing period.
 * Allowance = strategy call hours; completed calls (1h each) and approved
 * task estimates deduct from the same pool. Departments are a tag/filter,
 * not separate buckets.
 */
export const useTaskTime = () => {
  const { currentUser } = useAuth();
  const { companyId } = useCompanyId();
  const strategy = useStrategyCalls();

  const [accountId, setAccountId] = useState(null);
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      if (companyId) {
        if (active) setAccountId(companyId);
        return;
      }
      if (!currentUser?.email) {
        if (active) setAccountId(null);
        return;
      }
      try {
        const id = await findAccountIdByEmail(currentUser.email, 'Clients');
        if (active) setAccountId(id || null);
      } catch {
        if (active) setAccountId(null);
      }
    })();
    return () => {
      active = false;
    };
  }, [companyId, currentUser?.email]);

  const refresh = useCallback(async () => {
    if (!accountId) {
      setRequests([]);
      setRequestsLoading(false);
      return;
    }
    setRequestsLoading(true);
    try {
      const rows = await listTaskTimeRequests(accountId);
      setRequests(rows);
    } catch (e) {
      console.error('Error loading task time requests:', e);
      setRequests([]);
    } finally {
      setRequestsLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const allowanceHours =
    strategy.limit != null ? computeAllowanceFromCallLimit(strategy.limit) : null;

  const balance = useMemo(
    () =>
      computeTaskTimeBalance(requests, {
        allowanceHours,
        usedCallHours: Number(strategy.used) || 0,
        periodKey: strategy.periodKey,
        periodStart: strategy.periodStart,
        periodEnd: strategy.periodEnd,
      }),
    [requests, allowanceHours, strategy.used, strategy.periodKey, strategy.periodStart, strategy.periodEnd]
  );

  return {
    accountId,
    requests,
    allowanceHours,
    callLimit: strategy.limit,
    ...balance,
    periodKey: strategy.periodKey,
    periodDisplay: strategy.periodDisplay,
    periodEnd: strategy.periodEnd,
    loading: strategy.loading || requestsLoading,
    refresh,
  };
};
